import React, { useCallback, useMemo, useState } from 'react';

import { ReportGridFilterModelSchema } from '@aline/common';
import type { ReportGridFilterModel } from '@aline/common';
import { Badge, Popover, Typography } from '@mui/material';
import { getGridStringOperators, GridLogicOperator } from '@mui/x-data-grid-premium';
import type { GridColDef, GridFilterModel } from '@mui/x-data-grid-premium';
import { SelectButton } from 'src/components/generic';
import { FilterIcon } from 'src/components/icons/FilterIcon';
import { createBuiltInReportFilterColDef } from 'src/components/reports/helpers';
import { ReportFiltersPanelCore } from 'src/components/reports/report-filters-panel-core';
import { ReportColumnFileField } from 'src/gql/generated/apollo-types';
import { makeStyles } from 'tss-react/mui';

import { splitEditableFilterItems } from './question-plan-helpers';

const FILTERABLE_FILE_FIELDS = [
  ReportColumnFileField.Status,
  ReportColumnFileField.Type,
  ReportColumnFileField.CreatedAt,
  ReportColumnFileField.UpdatedAt,
  ReportColumnFileField.LastAccessedByRequesterAt,
  ReportColumnFileField.Owner,
  ReportColumnFileField.Labels,
  ReportColumnFileField.Parties,
  ReportColumnFileField.FileType,
  ReportColumnFileField.RequesterFavorited,
];

const useStyles = makeStyles()(theme => ({
  /** Grouped with the filter button: both describe the documents covered. */
  filterGroup: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface DocumentFilterControlsProps {
  disabled: boolean;
  filterModel: ReportGridFilterModel;
  onFilterModelChange: (filterModel: ReportGridFilterModel) => void;
  /** Left of the button: what the filters cover, such as a document count. */
  summary: React.ReactNode;
  /** The run joins filters the card never sees under this AND/OR, so it cannot change. */
  logicOperatorLocked?: boolean;
}

/**
 * A plan card's Filters button and the editor it opens, for the documents
 * the plan covers. Only document fields are editable. Filters on question
 * answers, which an analysis can set, stay on the model untouched: the
 * editor has no column for them, so it would show them as broken rows. They
 * join the edited filters under the model's AND/OR, so while there are any,
 * that AND/OR is not editable either.
 */
export const DocumentFilterControls = React.memo(function DocumentFilterControls({
  disabled,
  filterModel,
  logicOperatorLocked = false,
  onFilterModelChange,
  summary,
}: DocumentFilterControlsProps) {
  const { classes } = useStyles();
  const filterColumns = useMemo(buildFilterColumns, []);
  const editableFields = useMemo(
    () => new Set(filterColumns.map(column => column.field)),
    [filterColumns]
  );
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const { editableItems, preservedItems } = useMemo(
    () => splitEditableFilterItems(filterModel, editableFields),
    [editableFields, filterModel]
  );
  const gridFilterModel = useMemo<GridFilterModel>(
    () => ({
      ...filterModel,
      items: editableItems,
      logicOperator:
        filterModel.logicOperator === 'or' ? GridLogicOperator.Or : GridLogicOperator.And,
      quickFilterLogicOperator:
        filterModel.quickFilterLogicOperator === 'or'
          ? GridLogicOperator.Or
          : GridLogicOperator.And,
    }),
    [editableItems, filterModel]
  );

  const handleGridFilterModelChange = useCallback(
    (nextFilterModel: unknown) => {
      const parsed = ReportGridFilterModelSchema.safeParse(nextFilterModel);
      if (parsed.success)
        onFilterModelChange({ ...parsed.data, items: [...parsed.data.items, ...preservedItems] });
    },
    [onFilterModelChange, preservedItems]
  );

  return (
    <div className={classes.filterGroup}>
      {summary}
      <Badge
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        badgeContent={filterModel.items.length}
        color="primary"
        invisible={filterModel.items.length === 0}
        // Top left: the card clips at its right edge, which cut the badge in half there.
        sx={{ '& .MuiBadge-badge': { left: 2, top: 2 } }}
      >
        <SelectButton
          selected={Boolean(filterAnchorEl)}
          variant="outlined"
          onClick={event => {
            const anchor = event.currentTarget;
            setFilterAnchorEl(previous => (previous ? null : anchor));
          }}
        >
          <FilterIcon />
          <Typography sx={{ fontWeight: 600 }} variant="body2">
            Filters
          </Typography>
        </SelectButton>
      </Badge>
      {/* Hung from the button's right edge so the editor opens back across the card rather than off it. */}
      <Popover
        anchorEl={filterAnchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(filterAnchorEl)}
        /**
         * No width of its own: the compact rows size the paper, and MUI
         * keeps it inside the viewport. A fixed width would be wrong at
         * one docked panel width or another, and too wide a paper is
         * what clipped the row's delete button.
         */
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setFilterAnchorEl(null)}
      >
        <ReportFiltersPanelCore
          compact
          columns={filterColumns}
          disableAddFilterButton={disabled}
          disableLogicOperator={logicOperatorLocked || preservedItems.length > 0}
          disabled={disabled}
          filterModel={gridFilterModel}
          onFilterModelChange={handleGridFilterModelChange}
        />
      </Popover>
    </div>
  );
});

/**
 * Built on first render rather than at module load: the grid operators come
 * from the `src/components` barrel, and importing this card must not force
 * that whole tree to evaluate.
 */
function buildFilterColumns(): GridColDef[] {
  const descriptionColumn: GridColDef = {
    field: 'description',
    filterOperators: getGridStringOperators().filter(operator => operator.value !== 'isAnyOf'),
    filterable: true,
    headerName: 'Description',
    type: 'string',
  };

  return [
    createBuiltInReportFilterColDef(ReportColumnFileField.Name),
    descriptionColumn,
    ...FILTERABLE_FILE_FIELDS.map(createBuiltInReportFilterColDef),
  ];
}
